import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Carte, Champ, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { dateFr, montant, nomClient } from '../lib/format';
import type { Client, Reservation, Vehicule } from '../api/types';

/**
 * La liste des locations.
 *
 * Sans cet ecran, une reservation prise pour la semaine prochaine n'etait
 * atteignable par aucun chemin une fois l'ecran quitte : ni pour imprimer son
 * contrat, ni pour saisir la caution. Elle ne reapparaissait que le jour du
 * depart, dans la file du jour.
 */
const FILTRES = [
  { cle: 'en_cours', label: 'En cours', statuts: 'en_cours,non_restitue' },
  { cle: 'a_venir', label: 'À venir', statuts: 'option,confirmee' },
  { cle: 'terminees', label: 'Terminées', statuts: 'terminee' },
  { cle: 'toutes', label: 'Toutes', statuts: '' },
];

export default function Locations() {
  const { peut } = useAuth();
  const mobile = useMobile();
  const [filtre, setFiltre] = useState('en_cours');
  const [recherche, setRecherche] = useState('');
  const [liste, setListe] = useState<Reservation[] | null>(null);

  const charger = useCallback(() => {
    const f = FILTRES.find((x) => x.cle === filtre);
    setListe(null);
    api.get<Reservation[]>(`/reservations${qs({ statut: f?.statuts || undefined })}`).then(setListe);
  }, [filtre]);
  useEffect(charger, [charger]);

  // La recherche se fait ici : le patron cherche « la Clio » ou « Bouzidi »,
  // il ne sait pas si c'est un numero de contrat ou une plaque.
  const q = recherche.trim().toLowerCase();
  const visibles = (liste ?? []).filter((r) => {
    if (!q) return true;
    const v = r.vehicle as Vehicule;
    const c = r.customer as Client;
    return [r.numero, v?.marque, v?.modele, v?.immatriculation, nomClient(c)]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
  });

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Mes locations</h1>
          <p>Toutes vos locations, du devis au retour · cliquez pour ouvrir le dossier</p>
        </div>
        {peut('creer.reservation') && (
          <div className="actions">
            <Link className="btn btn-p" to="/reservations/nouvelle">Nouvelle location</Link>
          </div>
        )}
      </div>

      <Carte pad={false} titre={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {FILTRES.map((f) => (
            <button key={f.cle} className={`btn btn-s ${filtre === f.cle ? 'btn-p' : ''}`}
                    onClick={() => setFiltre(f.cle)}>{f.label}</button>
          ))}
        </div>
      } action={
        <Champ label="">
          <input placeholder="Numéro, client, marque ou plaque…" value={recherche}
                 onChange={(e) => setRecherche(e.target.value)} />
        </Champ>
      }>
        {liste === null ? (
          <div className="carte-p"><Squelette lignes={5} /></div>
        ) : visibles.length === 0 ? (
          <div className="carte-p">
            <Vide titre={q ? 'Aucune location ne correspond' : 'Aucune location ici'}
                  action={!q && peut('creer.reservation')
                    ? <Link className="btn btn-p btn-l" to="/reservations/nouvelle">Créer une location</Link>
                    : undefined}>
              {q
                ? <>Essayez avec le nom du client, la plaque, ou changez de filtre.</>
                : <>Changez de filtre pour voir les locations passées ou à venir.</>}
            </Vide>
          </div>
        ) : mobile ? (
          <div className="cartes">
            {visibles.map((r) => {
              const v = r.vehicle as Vehicule;
              return (
                <Link key={r._id} className="carte-ligne" to={`/reservations/${r._id}`}>
                  <div className="cl-tete">
                    <span className="cl-titre">{r.numero}</span>
                    <Badge statut={r.statut} />
                  </div>
                  <div className="cl-sous">{nomClient(r.customer as Client)} · {v?.marque} {v?.modele}</div>
                  <div className="cl-sous">{dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</div>
                  <div className="cl-pied montant">{montant(r.totalPrevuMinor)}</div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr>
                  <th>N°</th><th>Client</th><th>Véhicule</th><th>Période</th>
                  <th className="droite">Total prévu</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((r) => {
                  const v = r.vehicle as Vehicule;
                  return (
                    <tr key={r._id}>
                      <td><Link to={`/reservations/${r._id}`}><b>{r.numero}</b></Link></td>
                      <td>{nomClient(r.customer as Client)}</td>
                      <td>{v?.marque} {v?.modele} <span className="mini sourdine">{v?.immatriculation}</span></td>
                      <td className="mini sourdine">{dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</td>
                      <td className="droite montant">{montant(r.totalPrevuMinor)}</td>
                      <td><Badge statut={r.statut} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {visibles.length} location(s) affichée(s){q && ` sur ${(liste ?? []).length}`}.
        Le dossier d'une location porte son contrat, sa caution, son état des lieux et son solde.
      </p>
    </>
  );
}
